import React, { useState } from "react";
import { SearchModalWithTypes } from "../../../../core/component/Search/SearchModalWithTypes";
import { translate } from "../../../../core";
import { Combo } from "../../../../core/component";
import { ItemCategoryLevel, ItemType, Sex } from "../../../inventory/type";
import { getItemTypeComboOptions } from "../../../inventory/func/type-options";
import { Contact } from "../../../contact/type/contact";
import { stringConcat } from "../../../../core/until";
import { useLazyQuery } from "@apollo/client";
import { GetVendorsForLookup } from "../../../contact/graphql/contact.queries.graphql";
import {
  QueryPageableRequest,
  QueryPageableResponse,
} from "../../../../core/type";
import { ContactFindRequest } from "../../../contact/type/contact.query";
import { Form, Radio, Switch } from "antd";
import { MassChangeRequest, UpdateSelectionMode, SearchProps } from "./types";
import { ItemSearchCategorySingle } from '../../../inventory/component/ItemSearchCategorySingle'
import { SearchParameters } from "../../../../core/component/Search";
import { StylesByVendorAutoComplete } from "../../../inventory/component/StylesByVendorAutoComplete";
import { ItemSearchCategoryMulti } from "../../../inventory/component/ItemSearchCategoryMulti";

const SearchModal: React.FC<SearchProps> = ({
  searchOpen,
  setSearchOpen,
  onCompleteSearch,
}) => {
  const [findVendors] = useLazyQuery<
    QueryPageableResponse<Contact, "contactsFind">,
    QueryPageableRequest<ContactFindRequest>
  >(GetVendorsForLookup);

  const [updateSelectionMode, setUpdateSelecionMode] = useState<UpdateSelectionMode>(UpdateSelectionMode.Manual)

  return (
    <SearchModalWithTypes<MassChangeRequest>
      open={searchOpen}
      closeMe={() => setSearchOpen(false)}
      onComplete={value => onCompleteSearch(value, updateSelectionMode)}
      title={translate("mass_changes", 'search')}
      idLabel={translate("item", "/", "sku", "number_abbr")}
      searchOptions={[
        {
          key: "type",
          label: translate("type"),
          render: ({ isActive }) => (
            <Combo<any, ItemType>
              disabled={!isActive}
              allowMultiple
              size="middle"
              className="w-100"
              options={getItemTypeComboOptions()}
            />
          ),
          setValue: (params, request) => {
            request.types = params.value;
          },
          hideExactMatch: true,
        },
        {
          key: "gender",
          label: translate("gender"),
          render: ({ isActive }) => (
            <Combo<any, Sex>
              disabled={!isActive}
              size="middle"
              className="w-100"
              options={Object.entries(Sex).map(([key, value]) => ({
                value: value,
                display: value,
                label: key
              }))}
            />
          ),
          setValue: (params, request) => {
            request.gender = params.value;
          },
          hideExactMatch: true,
        },
        {
          key: "vendor",
          label: translate("vendor"),
          render: ({ isActive }) => (
            <Combo<Contact, number>
              disabled={!isActive}
              size="middle"
              className="w-100"
              fetch={async ({ request, search }) => {
                const { data } = await findVendors({
                  variables: {
                    request: {
                      take: request?.take ?? 100,
                      skip: request?.skip ?? 0,
                      searchVendor: search,
                    },
                  },
                });

                const list = data?.contactsFind.data ?? [];

                return list.map((e) => ({
                  value: e.id || 0,
                  display: stringConcat(e.contactId, " - ", e.company),
                  item: e || {},
                }));
              }}
            />
          ),
          hideExactMatch: true,
          setValue: (params, request) => {
            request.vendorId = params.value;
          },
        },
        {
          key: "style",
          label: translate("style", "/", "pattern"),
          setValue: (params, request) => {
            request.style = params.value;
            request.styleExactMatch = params.exactMatch;
          },
          formItemDisabled: true,
          render: ({ isActive, index }) => (
            <Form.Item
              noStyle
              shouldUpdate
            >
              {({ getFieldValue }) => {
                const params = getFieldValue(['parameters']) as SearchParameters[]
                const {value: vendorId, isActive: vendorIsActive} = params.find(p => p.key === 'vendor') ?? {}
                return <Form.Item 
                  noStyle
                  name={[index, 'value']}
                >
                  <StylesByVendorAutoComplete
                    vendorId={(vendorId && vendorIsActive) ? vendorId : undefined}
                    isActive={isActive}
                  />
                </Form.Item>
              }}
            </Form.Item>
          ),
        },
        {
          key: 'category',
          label: translate('category'),
          render: ({ isActive }) => (
            <ItemSearchCategoryMulti
              disabled={!isActive}
              level={ItemCategoryLevel.Category}
            />
          ),
          setValue: (params, request) => {
            request.categoryId = params.value
          },
          hideExactMatch: true,
        },
        {
          key: 'subcategory',
          label: translate('subcategory'),
          render: ({ isActive }) => (
            <ItemSearchCategoryMulti
              disabled={!isActive}
              level={ItemCategoryLevel.Subcategory}
            />
          ),
          setValue: (params, request) => {
            request.subcategoryId = params.value
          },
          hideExactMatch: true,
        },
        {
          key: 'subcategory2',
          label: translate('subcategory2'),
          render: ({ isActive }) => (
            <ItemSearchCategoryMulti
              disabled={!isActive}
              level={ItemCategoryLevel.Subcategory2}
            />
          ),
          setValue: (params, request) => {
            request.subcategory2Id = params.value
          },
          hideExactMatch: true,
        },
        {
          key: "patternNumber",
          label: translate("pattern_number_abbr"),
          setValue: (params, request) => {
            request.patternNumber = params.value;
            request.patternNumberExactMatch = params.exactMatch;
          },
        },
        {
          key: "showOnWeb",
          label: translate("show", "on", "web"),
          setValue: (params, request) => {
            request.showOnWeb = params.value;
          },
          render: ({ isActive }) => <Switch disabled={!isActive} />,
          hideExactMatch: true,
        },
        {
          key: "inStock",
          label: translate("in", "stock"),
          setValue: (params, request) => {
            request.inStock = params.value;
          },
          render: ({ isActive }) => <Switch disabled={!isActive} />,
          hideExactMatch: true,
        },
        {
          key: "nonPurchasable",
          label: translate("non_purchasable"),
          setValue: (params, request) => {
            request.nonPurchasable = params.value;
          },
          render: ({ isActive }) => <Switch disabled={!isActive} />,
          hideExactMatch: true,
        },
      ]}
      findPage={async (id) => {
        return {
          page: 0,
          error: undefined,
        };
      }}
      findPageLoading={false}
      sort={{
        fields: [
          { value: "sku", display: translate("sku") },
          { value: "style", display: translate("style") },
          { value: "category", display: translate("category") },
          { value: "subcategory", display: translate("subcategory") },
          { value: "subcategory2", display: translate("subcategory2") },
          { value: 'patternNumber', display: translate('pattern_number_abbr') },
        ],
      }}
      additonalComponents={[
        <Form.Item
          label={translate('selection', 'mode')}
        >
          <Radio.Group
            value={updateSelectionMode}
            onChange={e => setUpdateSelecionMode(e.target.value)}
            options={Object.entries(UpdateSelectionMode).map(([key, value]) => ({
              value: value,
              label: key
            }))}
          />
        </Form.Item>
      ]}
    />
  );
};

export default SearchModal;
